import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  House,
  FileText,
  Calendar,
  ListChecks,
  Users,
  ChatCircleDots,
  CurrencyDollar,
  SignOut,
  List,
  Gear,
  UploadSimple,
  Trash,
  BellRinging,
  Sparkle
} from '@phosphor-icons/react';

const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigationSections = [
    {
      title: 'MAIN',
      items: [
        { name: 'Dashboard', path: '/dashboard', icon: House, badge: null },
        { name: 'Applications', path: '/applications-list', icon: FileText, badge: 12 },
        { name: 'Calendar', path: '/calendar', icon: Calendar, badge: null },
      ]
    },
    {
      title: 'MANAGEMENT',
      items: [
        { name: 'Tasks', path: '/tasks', icon: ListChecks, badge: 18 },
        { name: 'Nudges', path: '/nudges', icon: BellRinging, badge: null },
        { name: 'Listers Intel', path: '/intel', icon: Sparkle, badge: 'AI' },
        { name: 'Buyers', path: '/buyers', icon: Users, badge: null },
        { name: 'Messages', path: '/messages', icon: ChatCircleDots, badge: 5 },
        { name: 'Payouts', path: '/payouts', icon: CurrencyDollar, badge: 8 },
      ]
    },
    ...(user?.role === 'admin' ? [{
      title: 'SYSTEM',
      items: [
        { name: 'Settings', path: '/settings', icon: Gear, badge: null },
        { name: 'Data Re-Entry', path: '/data-reentry', icon: UploadSimple, badge: null },
        { name: 'Recycle Bin', path: '/recycle-bin', icon: Trash, badge: null },
      ]
    }] : [])
  ];

  return (
    <div className="flex h-screen bg-listers-content-bg overflow-hidden">
      {/* Dark Sidebar - Desktop */}
      <aside className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64 bg-listers-sidebar border-r border-listers-sidebar-hover">
          {/* Logo */}
          <div className="flex items-center h-16 px-6 border-b border-listers-sidebar-hover">
            <img src="/listers-logo.png" alt="Listers" className="h-8 w-auto" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 overflow-y-auto">
            {navigationSections.map((section) => (
              <div key={section.title} className="mb-8">
                <p className="px-3 mb-3 text-xs font-bold tracking-[0.2em] text-listers-sidebar-text">
                  {section.title}
                </p>
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        data-testid={`nav-${item.name.toLowerCase()}`}
                        className={`
                          flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium
                          transition-colors duration-200
                          ${isActive 
                            ? 'bg-listers-sidebar-hover text-white' 
                            : 'text-listers-sidebar-text hover:bg-listers-sidebar-hover hover:text-listers-sidebar-text-hover'
                          }
                        `}
                      >
                        <div className="flex items-center">
                          <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                          <span className="ml-3">{item.name}</span>
                        </div>
                        {item.badge && (
                          <span className={`
                            px-2 py-0.5 text-xs font-semibold rounded-full
                            ${isActive 
                              ? 'bg-listers-green text-listers-sidebar' 
                              : 'bg-listers-sidebar-hover text-listers-sidebar-text'
                            }
                          `}>
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User Profile & Logout */}
          <div className="p-4 border-t border-listers-sidebar-hover">
            <div className="flex items-center justify-between">
              <div className="flex items-center min-w-0">
                <div className="flex-shrink-0 w-8 h-8 bg-listers-green rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-listers-sidebar">
                    {user?.name?.[0]?.toUpperCase() || 'A'}
                  </span>
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
                  <p className="text-xs text-listers-sidebar-text truncate">{user?.email || ''}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex-shrink-0 p-2 text-listers-sidebar-text hover:text-listers-sidebar-text-hover hover:bg-listers-sidebar-hover rounded-md transition-colors duration-200"
                data-testid="logout-button"
              >
                <SignOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 flex flex-col w-64 bg-listers-sidebar">
            {/* Mobile sidebar content (same as desktop) */}
            <div className="flex items-center h-16 px-6 border-b border-listers-sidebar-hover">
              <img src="/listers-logo.png" alt="Listers" className="h-8 w-auto" />
            </div>
            <nav className="flex-1 px-3 py-6 overflow-y-auto">
              {navigationSections.map((section) => (
                <div key={section.title} className="mb-8">
                  <p className="px-3 mb-3 text-xs font-bold tracking-[0.2em] text-listers-sidebar-text">
                    {section.title}
                  </p>
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`
                            flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium
                            transition-colors duration-200
                            ${isActive 
                              ? 'bg-listers-sidebar-hover text-white' 
                              : 'text-listers-sidebar-text hover:bg-listers-sidebar-hover hover:text-listers-sidebar-text-hover'
                            }
                          `}
                        >
                          <div className="flex items-center">
                            <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                            <span className="ml-3">{item.name}</span>
                          </div>
                          {item.badge && (
                            <span className={`
                              px-2 py-0.5 text-xs font-semibold rounded-full
                              ${isActive 
                                ? 'bg-listers-green text-listers-sidebar' 
                                : 'bg-listers-sidebar-hover text-listers-sidebar-text'
                              }
                            `}>
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
            <div className="p-4 border-t border-listers-sidebar-hover">
              <div className="flex items-center justify-between">
                <div className="flex items-center min-w-0">
                  <div className="flex-shrink-0 w-8 h-8 bg-listers-green rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-listers-sidebar">
                      {user?.name?.[0]?.toUpperCase() || 'A'}
                    </span>
                  </div>
                  <div className="ml-3 min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
                    <p className="text-xs text-listers-sidebar-text truncate">{user?.email || ''}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex-shrink-0 p-2 text-listers-sidebar-text hover:text-listers-sidebar-text-hover hover:bg-listers-sidebar-hover rounded-md transition-colors duration-200"
                >
                  <SignOut size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <div className="flex md:hidden items-center justify-between h-16 px-4 bg-listers-sidebar border-b border-listers-sidebar-hover">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-white hover:bg-listers-sidebar-hover rounded-md transition-colors"
            aria-label="Open menu"
            data-testid="mobile-menu-button"
          >
            <List size={28} weight="bold" />
          </button>
          <img src="/listers-logo.png" alt="Listers" className="h-7 w-auto" />
          <div className="w-10" /> {/* Spacer for centering */}
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
